import React from "react"
import { useDroppable } from "@dnd-kit/core"
import { makeStyles } from "@mui/styles"

import ProdPlannerTile from "./ProdPlannerTile"
import { BLOCK_HEIGHT, HEADER_BLOCK_HEIGHT, TOTAL_BLOCKS } from "../../utils/constant"
import { COLORS } from "../../utils"

/**
 * Machine column component showing header and droppable time blocks
 * Renders tiles for a specific machine in the production planner
 */
export default function MachineColumn({
  machineId,
  machineIndex = 0,
  machineLabel,
  machineLength,
  tiles = [],
  setTiles,
  previewTiles = [],
  onResize,
  isAnyTileDragging = false,
}) {
  const classes = useStyles()

  const columnTiles = tiles.filter((s) => s.machine === machineId)
  // Merge regular tiles with preview tiles (avoid duplicates)
  const allTilesToRender = [
    ...columnTiles.filter((s) => !previewTiles.some((p) => p.objectId === s.objectId)),
    ...previewTiles,
  ]

  return (
    <div className={classes.columnWrapper}>
      <div className={classes.machineColumn}>
        {/* Render header and time blocks */}
        {Array.from({ length: TOTAL_BLOCKS }).map((_, blockIndex) => {
          const id = `machine-${machineId}__${blockIndex}`
          const { setNodeRef } = useDroppable({ id })
          const isHour = blockIndex % 4 === 0

          // First block is the machine header
          if (blockIndex === 0) {
            return (
              <div
                key={id}
                ref={setNodeRef}
                id={id}
                className={classes.firstCell}
                style={{
                  borderTopLeftRadius: machineIndex === 0 ? "12px" : 0,
                  borderTopRightRadius: machineIndex === machineLength - 1 ? "12px" : 0,
                }}
              >
                {machineLabel}
              </div>
            )
          }

          // Regular time block
          return (
            <div
              key={id}
              ref={setNodeRef}
              id={id}
              className={`${classes.cell} ${isHour ? classes.cellHour : ""}`}
              style={{
                height: BLOCK_HEIGHT,
                borderLeft: machineIndex === 0 ? "1px solid #DBDBDB" : undefined,
              }}
            />
          )
        })}

        {/* Render tiles absolutely positioned within the column */}
        {allTilesToRender.map((tile) => (
          <ProdPlannerTile
            key={tile.objectId}
            tile={tile}
            onResize={onResize}
            tiles={tiles}
            setTiles={setTiles}
            isAnyTileDragging={isAnyTileDragging}
          />
        ))}
      </div>
    </div>
  )
}

const useStyles = makeStyles(() => ({
  columnWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    flex: "1 1 116px", 
    minWidth: "116px",
  },
  machineColumn: {
    position: "relative",
    width: "100%",
  },
  cell: {
    boxSizing: "border-box",
    backgroundColor: COLORS.WHITE,
    borderRight: `1px solid ${COLORS.BLACK_200}`,
  },
  cellHour: {
    borderBottom: `1px solid ${COLORS.BLACK_200}`,
  },
  firstCell: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: HEADER_BLOCK_HEIGHT,
    padding: "16px",
    backgroundColor: COLORS.PRIMARY_BLUE_20,
    color: COLORS.BLACK800,
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "22px",
    textAlign: "center",
    boxSizing: "border-box",
    position: "sticky", // Keeps header visible when scrolling
    top: 0,
    zIndex: 10,
  },
}))
